import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {signinRedirect, signoutRedirect} from "../config/teamsystemOidc";
import {refreshAccessToken} from "../services/authService";
import {TOKENS_UPDATED_EVENT} from "../utils/apiClient";
import {
  clearTokens,
  decodeToken,
  getAccessToken,
  getAccessTokenExpiry,
  getRefreshToken,
  isAccessTokenExpired,
  setTokens
} from "../utils/identityTokens";

/**
 * Fuente de verdad de la sesión de Identity. Reemplaza a useAuth() de
 * react-oidc-context: la "sesión" ya no es la de Teamsystem sino la de los
 * tokens internos de Identity almacenados (sessionStorage + localStorage).
 *
 * Expone el estado reactivo (isLoading, isAuthenticated, accessToken) y las
 * acciones de login/logout/refresh. El callback usa completeLogin() tras el
 * token exchange para registrar los tokens y marcar la sesión como activa.
 */
const IdentityAuthContext = createContext(undefined);

const STORAGE_KEY = "returnUrl";

const PROACTIVE_REFRESH_MARGIN_MS = 60_000;

export const IdentityAuthProvider = ({children}) => {
  const [isLoading, setIsLoading] = useState(true);
  // Estado que dispara re-render cuando cambian los tokens.
  const [accessToken, setAccessToken] = useState(() => getAccessToken());
  // Evita refrescos en paralelo (StrictMode monta dos veces y el interceptor
  // también puede pedir refresh).
  const refreshPromiseRef = useRef(null);

  const syncFromStorage = useCallback(() => {
    setAccessToken(getAccessToken());
  }, []);

  /** Renueva el access_token usando el refresh_token persistido. */
  const refresh = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("No hay refresh_token disponible");

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = refreshAccessToken(refreshToken)
        .then((tokens) => {
          setTokens(tokens);
          syncFromStorage();
          return tokens;
        })
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }

    return refreshPromiseRef.current;
  }, [syncFromStorage]);

  /** Registra los tokens obtenidos en el token exchange (lo llama el callback). */
  const completeLogin = useCallback(
    (tokens) => {
      setTokens(tokens);
      syncFromStorage();
    },
    [syncFromStorage]
  );

  /**
   * Inicia el login redirigiendo a Teamsystem.
   * @param {string} returnUrl URL a la que volver tras autenticarse.
   * @param {{forceLogin?: boolean}} [options] Con forceLogin=true añade prompt=login
   *   a la URL de authorize para obligar a TS ID a reautenticar (p. ej. tras un
   *   usuario sin acceso: evita el re-login silencioso vía SSO).
   */
  const login = useCallback(async (returnUrl = "/dashboard", {forceLogin = false} = {}) => {
    if (returnUrl) sessionStorage.setItem(STORAGE_KEY, returnUrl);
    await signinRedirect(forceLogin ? {extraQueryParams: {prompt: "login"}} : undefined);
  }, []);

  /** Cierra sesión: limpia tokens internos y cierra sesión en Teamsystem. */
  const logout = useCallback(async () => {
    clearTokens();
    sessionStorage.removeItem(STORAGE_KEY);
    syncFromStorage();
    await signoutRedirect();
  }, [syncFromStorage]);

  // Rehidratación al montar: si no hay access_token válido pero hay refresh_token,
  // intenta renovar (p. ej. tras cerrar/reabrir la pestaña → sessionStorage vacío).
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!isAccessTokenExpired()) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      if (getRefreshToken()) {
        try {
          await refresh();
        } catch {
          clearTokens();
          if (!cancelled) syncFromStorage();
        }
      } else {
        clearTokens();
        if (!cancelled) syncFromStorage();
      }
      if (!cancelled) setIsLoading(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [refresh, syncFromStorage]);

  // Sincroniza el estado cuando el interceptor de apiClient renueva los tokens
  // (el interceptor vive fuera de React y solo puede escribir en storage).
  useEffect(() => {
    const onTokensUpdated = () => syncFromStorage();
    window.addEventListener(TOKENS_UPDATED_EVENT, onTokensUpdated);
    return () => window.removeEventListener(TOKENS_UPDATED_EVENT, onTokensUpdated);
  }, [syncFromStorage]);

  // Refresco proactivo: renueva el access_token poco antes de que expire
  useEffect(() => {
    if (!accessToken) return undefined;

    const expiry = getAccessTokenExpiry();
    if (!expiry) return undefined;

    const msUntilRefresh = expiry - Date.now() - PROACTIVE_REFRESH_MARGIN_MS;
    const timer = setTimeout(
      () => {
        if (!getRefreshToken()) return;
        refresh().catch(() => {
          clearTokens();
          syncFromStorage();
        });
      },
      Math.max(msUntilRefresh, 0)
    );

    return () => clearTimeout(timer);
  }, [accessToken, refresh, syncFromStorage]);

  const isAuthenticated = Boolean(accessToken) && !isAccessTokenExpired();

  // Datos de usuario derivados del access_token (claims name/email).
  const user = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeToken(accessToken);
    if (!payload || (!payload.name && !payload.email)) return null;
    return {name: payload.name ?? null, email: payload.email ?? null};
  }, [accessToken]);

  const value = useMemo(
    () => ({isLoading, isAuthenticated, accessToken, user, login, logout, completeLogin, refresh}),
    [isLoading, isAuthenticated, accessToken, user, login, logout, completeLogin, refresh]
  );

  return <IdentityAuthContext.Provider value={value}>{children}</IdentityAuthContext.Provider>;
};

/** Hook de acceso a la sesión de Identity. */
export const useIdentityAuth = () => {
  const ctx = useContext(IdentityAuthContext);
  if (ctx === undefined) {
    throw new Error("useIdentityAuth debe usarse dentro de <IdentityAuthProvider>");
  }
  return ctx;
};

export default IdentityAuthContext;
